import {
  Eye,
  Loader2,
  Trash2,
  MoreVertical,
  Edit,
  Star,
  Zap,
  Shield,
  RefreshCw,
  ShieldAlert,
  Copy,
  CopyPlus,
  AlertTriangle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getIntegrationImage } from '@/lib/utils/integration-utils';
import { TpaLiveStatus } from '@/components/tagadapay/tpa-live-status';
import { PaymentFlowItemWithProcessors } from '@/lib/api/paymentFlows';
import { Store, useUpdateStore } from '@/lib/api/stores';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { toast } from 'react-hot-toast';

interface PaymentFlowCardProps {
  flow: PaymentFlowItemWithProcessors;
  onDelete: (flow: PaymentFlowItemWithProcessors) => void;
  onDuplicate: (flow: PaymentFlowItemWithProcessors) => void;
  store: Store;
}

const getUniqueProcessors = (flow: any) => {
  const processors = new Map();

  flow.processorConfigs?.forEach((config: any) => {
    if (config.processor && !config.disabled) {
      processors.set(config.processor.id, config.processor);
    }
  });

  flow.fallbackProcessorConfigs?.forEach((config: any) => {
    if (config.processor) {
      processors.set(config.processor.id, config.processor);
    }
  });

  return Array.from(processors.values());
};

const isSmartRetryEnabledAndValid = (flow: any) => {
  const rebillSettings = flow.rebillSettings;
  if (!rebillSettings) return false;
  if (!rebillSettings.customRetries || rebillSettings.customRetries.length === 0) return false;
  return true;
};

const getSmartRetrySummary = (flow: any) => {
  const rebillSettings = flow.rebillSettings;
  if (!rebillSettings) return null;

  const retryCount = rebillSettings.customRetries?.length || 0;
  const isAdvanced = rebillSettings.isAdvancedEnabled || false;

  return {
    retryCount,
    isAdvanced,
  };
};

const isAbuseDetectionEnabledAndValid = (flow: any) => {
  const abuseDetectionConfig = flow.abuseDetectionConfig;
  if (!abuseDetectionConfig) return false;
  return abuseDetectionConfig.enabled === true;
};

const getAbuseDetectionSummary = (flow: any) => {
  const abuseDetectionConfig = flow.abuseDetectionConfig;
  if (!abuseDetectionConfig || !abuseDetectionConfig.enabled) return null;

  const detectionMethods = [];
  if (abuseDetectionConfig.detectIpAddress) detectionMethods.push('IP');
  if (abuseDetectionConfig.detectBin) detectionMethods.push('BIN');
  if (abuseDetectionConfig.detectEmail) detectionMethods.push('Email');

  return {
    detectionMethods,
    timeWindow: abuseDetectionConfig.timeWindowMinutes || 10,
  };
};

// Check if flow has any archived processors
const hasArchivedProcessors = (flow: any) => {
  const archivedInPrimary = flow.processorConfigs?.some((c: any) => c.processor?.archivedAt);
  const archivedInFallback = flow.fallbackProcessorConfigs?.some((c: any) => c.processor?.archivedAt);
  return archivedInPrimary || archivedInFallback;
};

export const PaymentFlowCard = ({ flow, onDelete, onDuplicate, store }: PaymentFlowCardProps) => {
  const { t } = useTranslation(['paymentFlows', 'common']);
  const { orgRoutes, navigate: orgNavigate } = useOrgNavigate();
  const processors = getUniqueProcessors(flow);
  const { mutate: updateStore, isPending: isUpdatingStore } = useUpdateStore();
  const smartRetryEnabled = isSmartRetryEnabledAndValid(flow);
  const abuseDetectionEnabled = isAbuseDetectionEnabledAndValid(flow);
  const [showActivateDialog, setShowActivateDialog] = useState(false);

  const onSelect = () => {
    updateStore({
      storeId: store.id,
      updatedData: {
        name: store.name,
        presentmentCurrencies: store.presentmentCurrencies,
        chargeCurrencies: store.chargeCurrencies,
        baseCurrency: store.baseCurrency,
        selectedPaymentFlowId: flow.id,
      },
    });
    setShowActivateDialog(false);
  };

  const isDefault = store.selectedPaymentFlowId === flow.id;
  const hasArchived = hasArchivedProcessors(flow);

  // Count active features
  const activeFeatures = [
    flow.threeDsEnabled,
    flow.fallbackMode,
    flow.stickyProcessorEnabled,
    smartRetryEnabled,
    abuseDetectionEnabled,
  ].filter(Boolean).length;

  return (
    <>
      <Card
        className={cn(
          'group relative flex h-full cursor-pointer flex-col gap-0 overflow-hidden py-0 transition-all hover:shadow-md',
          isDefault ? 'border-emerald-500 shadow-sm' : 'hover:border-muted-foreground/30',
        )}
        onClick={() => orgNavigate(orgRoutes.PAYMENT_FLOW_DETAILS(flow.id))}
      >
        <CardHeader className="p-4 pb-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <CardTitle className="truncate text-base font-semibold">{flow.name}</CardTitle>
              {isDefault && (
                <Badge className="shrink-0 border-0 bg-emerald-100 px-1.5 py-0 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                  {t('paymentFlows:badges.default')}
                </Badge>
              )}
              {hasArchived && (
                <Badge 
                  variant="outline" 
                  className="shrink-0 gap-1 border-amber-300 bg-amber-100 px-1.5 py-0 text-[10px] font-medium text-amber-700 dark:border-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                  title={t('paymentFlows:warnings.has_archived_processors')}
                >
                  <AlertTriangle className="size-3" />
                </Badge>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuItem asChild>
                  <Link to={orgRoutes.PAYMENT_FLOW_DETAILS(flow.id)} className="cursor-pointer">
                    <Eye className="mr-2 size-4" />
                    {t('paymentFlows:view_details')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={orgRoutes.PAYMENT_FLOW_EDIT(flow.id)} className="cursor-pointer">
                    <Edit className="mr-2 size-4" />
                    {t('common:edit')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(flow.id);
                    toast.success(t('common:copied'));
                  }}
                  className="cursor-pointer"
                >
                  <Copy className="mr-2 size-4" />
                  {t('common:copy_id')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate(flow);
                  }}
                  className="cursor-pointer"
                >
                  <CopyPlus className="mr-2 size-4" />
                  {t('paymentFlows:duplicate.action')}
                </DropdownMenuItem>
                {!isDefault && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActivateDialog(true);
                      }}
                      className="cursor-pointer"
                    >
                      <Star className="mr-2 size-4" />
                      {t('paymentFlows:set_as_default')}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(flow);
                  }}
                  className="text-destructive focus:text-destructive cursor-pointer"
                  disabled={isDefault}
                >
                  <Trash2 className="mr-2 size-4" />
                  {t('common:delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>

        <CardContent className="flex-1 space-y-3 p-4 pt-0">
          {/* Feature badges + strategy in one row */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground mr-1 text-sm">
              {flow.strategy === 'weighted'
                ? t('paymentFlows:badges.custom_routing')
                : t('paymentFlows:badges.auto_routing')}
            </span>
            {flow.threeDsEnabled && (
              <Badge
                variant="secondary"
                className="gap-1.5 border-0 bg-blue-100 px-2 py-0.5 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
              >
                <Shield className="size-3.5" />
                {t('paymentFlows:badges.3ds')}
              </Badge>
            )}
            {flow.fallbackMode && (
              <Badge
                variant="secondary"
                className="gap-1.5 border-0 bg-amber-100 px-2 py-0.5 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
              >
                <RefreshCw className="size-3.5" />
                {t('paymentFlows:badges.fallback_retries', { count: flow.maxFallbackRetries ?? 0 })}
              </Badge>
            )}
            {smartRetryEnabled && (
              <Badge
                variant="secondary"
                className="gap-1.5 border-0 bg-purple-100 px-2 py-0.5 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300"
              >
                <Zap className="size-3.5" />
                {t('paymentFlows:badges.smart_retry')}
              </Badge>
            )}
            {abuseDetectionEnabled && (
              <Badge
                variant="secondary"
                className="gap-1.5 border-0 bg-rose-100 px-2 py-0.5 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300"
              >
                <ShieldAlert className="size-3.5" />
                {t('paymentFlows:badges.fraud_protection')}
              </Badge>
            )}
          </div>

          {/* Processors */}
          {processors.length > 0 && (
            <div className="space-y-1.5">
              <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                {t('paymentFlows:card.processors_heading')}
              </span>
              <div className="flex flex-wrap gap-2">
                {processors.slice(0, 4).map((processor) => (
                  <Link
                    key={processor.id}
                    to={orgRoutes.PROCESSOR_DETAILS(processor.id)}
                    className="bg-muted/30 hover:bg-accent hover:border-primary/20 flex items-center gap-2 rounded-md border px-2.5 py-1.5 transition-all"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={getIntegrationImage(processor.type)}
                      alt={processor.type}
                      className="size-4 object-contain"
                    />
                    <span className="max-w-[120px] truncate text-sm font-medium">{processor.name}</span>
                    <TpaLiveStatus
                      tpaId={processor.tagadapayAccountId}
                      status={processor.tagadapayStatus}
                      mode={processor.tagadapayMode}
                      compact
                    />
                  </Link>
                ))}
                {processors.length > 4 && (
                  <span className="text-muted-foreground flex items-center px-2 py-1 text-xs">
                    {t('paymentFlows:card.more_processors', { count: processors.length - 4 })}
                  </span>
                )}
              </div>
            </div>
          )}
        </CardContent>

        {!isDefault && (
          <CardFooter className="p-3 pt-0">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                setShowActivateDialog(true);
              }}
              disabled={isUpdatingStore}
            >
              {isUpdatingStore ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Star className="mr-2 size-4" />
              )}
              {t('paymentFlows:set_as_default')}
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* Set as Default Confirmation Dialog */}
      <AlertDialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Star className="size-5 text-emerald-600" />
              {t('paymentFlows:activate_dialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p>{t('paymentFlows:activate_dialog.description', { flowName: flow.name })}</p>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/20">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>{t('paymentFlows:activate_dialog.warning_title')}</strong>{' '}
                  {t('paymentFlows:activate_dialog.warning_message')}
                </p>
              </div>
              {processors.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium">
                    {t('paymentFlows:activate_dialog.processors_label')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {processors.map((processor) => (
                      <Badge key={processor.id} variant="outline" className="text-xs">
                        {processor.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onSelect} disabled={isUpdatingStore}>
              {isUpdatingStore ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              {t('paymentFlows:activate_dialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
